// Formulário de criar/editar proposta CRM (Story 11.6).
// - RHF + zodResolver (createCrmProposalSchema do @control-center/shared — fonte única)
// - Pickers: produto SaaS (obrigatório), plano (obrigatório), ciclo de cobrança
// - Vínculo: cliente OU lead OU deal (pré-preenche quando vem de um deal — AC3/?dealId)
// - Valores: mensal / anual / setup (opcionais, > 0 se preenchidos)
// - Desconto: amount + type + reason (reason+type obrigatórios se amount — DR-024)
// - Validade obrigatória (>= hoje, validado no Zod)
// - Submit desabilitado enquanto pending (AC10)
//
// NÃO edita status/proposalNumber (gerados/controlados pelo backend e ciclo 11.7).

import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import type { z } from 'zod';

import { createCrmProposalSchema } from '@control-center/shared';

import { firstErrorMessage } from '../../lib/form-errors';
import { Field, Input, Select, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';

const BILLING_OPTIONS: { value: string; label: string }[] = [
  { value: 'mensal', label: 'Mensal' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
  { value: 'pagamento_unico', label: 'Pagamento único' },
];

const DISCOUNT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Sem desconto' },
  { value: 'percent', label: 'Percentual (%)' },
  { value: 'fixed', label: 'Valor fixo (R$)' },
];

export interface ProposalOption {
  id: string;
  name: string;
}

export type ProposalFormValues = z.input<typeof createCrmProposalSchema>;
export type ProposalFormSubmit = z.output<typeof createCrmProposalSchema>;

interface ProposalFormProps {
  initialValues?: Partial<ProposalFormValues>;
  isPending?: boolean;
  submitLabel?: string;
  saasProducts?: ProposalOption[];
  plans?: ProposalOption[];
  /** Nome do vínculo (read-only) quando a proposta nasce de um deal/lead/cliente. */
  linkedDealTitle?: string | null;
  linkedLeadName?: string | null;
  linkedClientName?: string | null;
  onSubmit: (values: ProposalFormSubmit) => void | Promise<void>;
  onCancel?: () => void;
}

const DEFAULT_VALUES: ProposalFormValues = {
  deal_id: null,
  lead_id: null,
  client_id: null,
  saas_product_id: '',
  plan_id: '',
  billing_cycle: 'mensal',
  monthly_value: null,
  annual_value: null,
  setup_value: null,
  discount_amount: null,
  discount_type: null,
  discount_reason: null,
  valid_until: '',
  observations: null,
};

function SectionTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <h2 className="mb-3 text-[13px] font-semibold tracking-[-0.005em] text-ink">{children}</h2>
  );
}

function Card({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <section className="rounded-[14px] border border-line bg-panel p-[20px]">{children}</section>
  );
}

function emptyToNull(v: string): string | null {
  return v === '' ? null : v;
}

/** Valor monetário: string vazia → null; senão número (positivo validado no Zod). */
function valueToNumber(v: string): number | null {
  if (v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

export function ProposalForm({
  initialValues,
  isPending,
  submitLabel = 'Salvar',
  saasProducts = [],
  plans = [],
  linkedDealTitle,
  linkedLeadName,
  linkedClientName,
  onSubmit,
  onCancel,
}: ProposalFormProps): JSX.Element {
  const merged: ProposalFormValues = { ...DEFAULT_VALUES, ...initialValues };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ProposalFormValues>({
    resolver: zodResolver(createCrmProposalSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: merged,
  });

  const firstFieldRef = useRef<HTMLSelectElement | null>(null);
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  // Mostra os campos de desconto só quando há tipo selecionado (DR-024).
  const discountType = watch('discount_type');
  const hasDiscount = discountType === 'percent' || discountType === 'fixed';

  const { ref: saasRefRHF, ...saasRest } = register('saas_product_id', { setValueAs: emptyToNull });

  const handleValid = (values: ProposalFormValues): void | Promise<void> => {
    const parsed = createCrmProposalSchema.parse(values);
    return onSubmit(parsed);
  };

  return (
    <form
      onSubmit={handleSubmit(handleValid, (errs) =>
        toast.error(firstErrorMessage(errs) ?? 'Formulário tem campos inválidos.'),
      )}
      className="flex flex-col gap-4"
      noValidate
    >
      {/* Produto e ciclo */}
      <Card>
        <SectionTitle>Produto e ciclo</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {(linkedDealTitle || linkedLeadName || linkedClientName) && (
            <Field label="Vinculado a" className="md:col-span-2">
              {() => (
                <div className="flex flex-wrap gap-2 text-[13px] text-ink-2">
                  {linkedDealTitle && (
                    <span className="inline-flex items-center rounded-[6px] border border-line px-2 py-[3px]">
                      Deal: {linkedDealTitle}
                    </span>
                  )}
                  {linkedLeadName && (
                    <span className="inline-flex items-center rounded-[6px] border border-line px-2 py-[3px]">
                      Lead: {linkedLeadName}
                    </span>
                  )}
                  {linkedClientName && (
                    <span className="inline-flex items-center rounded-[6px] border border-line px-2 py-[3px]">
                      Cliente: {linkedClientName}
                    </span>
                  )}
                </div>
              )}
            </Field>
          )}

          <Field label="Produto SaaS" required error={errors.saas_product_id?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.saas_product_id}
                defaultValue={merged.saas_product_id ?? ''}
                ref={(el) => {
                  saasRefRHF(el);
                  firstFieldRef.current = el;
                }}
                {...saasRest}
              >
                <option value="">Selecione…</option>
                {saasProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Plano" required error={errors.plan_id?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.plan_id}
                defaultValue={merged.plan_id ?? ''}
                {...register('plan_id', { setValueAs: emptyToNull })}
              >
                <option value="">Selecione…</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Ciclo de cobrança" error={errors.billing_cycle?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.billing_cycle}
                {...register('billing_cycle')}
              >
                {BILLING_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Validade"
            required
            error={errors.valid_until?.message}
            hint="A proposta vale até esta data"
          >
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                type="date"
                hasError={!!errors.valid_until}
                {...register('valid_until')}
              />
            )}
          </Field>
        </div>
      </Card>

      {/* Valores */}
      <Card>
        <SectionTitle>Valores</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Mensal (R$)" error={errors.monthly_value?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                tabular
                inputMode="decimal"
                type="number"
                step="0.01"
                min="0"
                placeholder="0,00"
                hasError={!!errors.monthly_value}
                {...register('monthly_value', { setValueAs: valueToNumber })}
              />
            )}
          </Field>

          <Field label="Anual (R$)" error={errors.annual_value?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                tabular
                inputMode="decimal"
                type="number"
                step="0.01"
                min="0"
                placeholder="0,00"
                hasError={!!errors.annual_value}
                {...register('annual_value', { setValueAs: valueToNumber })}
              />
            )}
          </Field>

          <Field label="Setup (R$)" error={errors.setup_value?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                tabular
                inputMode="decimal"
                type="number"
                step="0.01"
                min="0"
                placeholder="0,00"
                hasError={!!errors.setup_value}
                {...register('setup_value', { setValueAs: valueToNumber })}
              />
            )}
          </Field>
        </div>
      </Card>

      {/* Desconto */}
      <Card>
        <SectionTitle>Desconto (opcional)</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Tipo de desconto" error={errors.discount_type?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.discount_type}
                defaultValue=""
                {...register('discount_type', { setValueAs: emptyToNull })}
              >
                {DISCOUNT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {hasDiscount && (
            <>
              <Field
                label={discountType === 'percent' ? 'Desconto (%)' : 'Desconto (R$)'}
                error={errors.discount_amount?.message}
              >
                {({ inputId, describedBy }) => (
                  <Input
                    id={inputId}
                    aria-describedby={describedBy}
                    tabular
                    inputMode="decimal"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0,00"
                    hasError={!!errors.discount_amount}
                    {...register('discount_amount', { setValueAs: valueToNumber })}
                  />
                )}
              </Field>

              <Field
                label="Motivo do desconto"
                required
                error={errors.discount_reason?.message}
                className="md:col-span-2"
              >
                {({ inputId, describedBy }) => (
                  <Input
                    id={inputId}
                    aria-describedby={describedBy}
                    placeholder="Ex: parceria de longo prazo"
                    hasError={!!errors.discount_reason}
                    {...register('discount_reason', { setValueAs: emptyToNull })}
                  />
                )}
              </Field>
            </>
          )}
        </div>
      </Card>

      {/* Observações */}
      <Card>
        <SectionTitle>Observações</SectionTitle>
        <Field label="Anotações internas" error={errors.observations?.message}>
          {({ inputId, describedBy }) => (
            <Textarea
              id={inputId}
              aria-describedby={describedBy}
              rows={3}
              placeholder="Contexto da proposta, condições combinadas, etc."
              hasError={!!errors.observations}
              {...register('observations', { setValueAs: emptyToNull })}
            />
          )}
        </Field>
      </Card>

      <div className="sticky bottom-0 -mx-[22px] flex items-center justify-end gap-2 border-t border-line bg-page/90 px-[22px] py-3 backdrop-blur">
        {onCancel && (
          <PillButton onClick={onCancel} disabled={isPending || isSubmitting}>
            Cancelar
          </PillButton>
        )}
        <PillButton solid type="submit" disabled={isPending || isSubmitting || !isValid}>
          {isPending || isSubmitting ? 'Salvando…' : submitLabel}
        </PillButton>
      </div>
    </form>
  );
}
